import type { Topic } from '../types/curriculumTopic';
import { isTopicDurationUnset } from './topicDurationHistory';
import type { TimelineTopicItem } from '../types/topicQuiz';
import {
  createDefaultQuiz,
  normalizeTopicQuizPlan,
} from '../types/topicQuiz';

const PATH_KEY = 'codelive-curriculum-path';

export function saveCurriculumPathForTimeline(topics: Topic[]): void {
  sessionStorage.setItem(PATH_KEY, JSON.stringify(topics));
}

/** Read path without clearing (used when returning to Curriculum Builder). */
export function loadCurriculumPath(): Topic[] {
  try {
    const raw = sessionStorage.getItem(PATH_KEY);
    if (!raw) return [];
    const topics = JSON.parse(raw) as Topic[];
    return Array.isArray(topics) ? topics : [];
  } catch {
    return [];
  }
}

export function clearCurriculumPath(): void {
  sessionStorage.removeItem(PATH_KEY);
}

/** Convert planner timeline items → Active Learning Path topics (keep durations). */
export function plannerTopicsToCurriculumTopics(
  topics: TimelineTopicItem[],
): Topic[] {
  return topics.map((t) => ({
    id: t.id,
    title: t.title,
    description: t.description || '',
    durationMinutes:
      t.durationMinutes != null && t.durationMinutes > 0
        ? t.durationMinutes
        : null,
    category: 'fundamentals' as const,
    workshopDayIndex: t.workshopDayIndex,
    ...(t.subtopics?.length
      ? {
          subtopics: t.subtopics.map((s) => ({
            id: s.id,
            title: s.title,
            durationMinutes: s.durationMinutes,
            selected: true,
          })),
        }
      : {}),
  }));
}

/**
 * Map curriculum path → timeline rows.
 * Omits selected subtopics with no planned time so they cannot reach the session.
 * Does not clear storage (caller may clear after successful import).
 */
export function curriculumPathToTimelineTopics(
  topics: Topic[],
): TimelineTopicItem[] {
  return topics
    .map((t, index) => {
      const selectedSubs = (t.subtopics?.filter((s) => s.selected) ?? []).filter(
        (s) => (Number(s.durationMinutes) || 0) > 0,
      );
      const durationFromSubs = selectedSubs.reduce(
        (sum, s) => sum + (Number(s.durationMinutes) || 0),
        0,
      );
      return {
        id: t.id,
        index: index + 1,
        title: t.title,
        description: t.description || '',
        durationMinutes: durationFromSubs,
        workshopDayIndex: t.workshopDayIndex,
        subtopics: selectedSubs.map((s, si) => ({
          id: s.id,
          index: si,
          title: s.title,
          durationMinutes: s.durationMinutes,
          // Required post-quiz from the first import (pre stays optional).
          postQuiz: createDefaultQuiz('post'),
        })),
      };
    })
    .filter((t) => (t.subtopics?.length ?? 0) > 0)
    .map((t, index) => ({ ...t, index: index + 1 }));
}

function mergeSubtopicLists(
  imported: TimelineTopicItem['subtopics'],
  previous: TimelineTopicItem['subtopics'],
): TimelineTopicItem['subtopics'] {
  if (!imported?.length) return previous;
  const prevById = new Map((previous ?? []).map((s) => [s.id, s]));
  return imported.map((sub, index) => {
    const prev = prevById.get(sub.id);
    return {
      ...sub,
      index,
      // Keep quiz enablement from the planner when the curriculum path re-imports
      preQuiz: prev?.preQuiz,
      postQuiz: prev?.postQuiz ?? sub.postQuiz ?? createDefaultQuiz('post'),
    };
  });
}

/**
 * Keep quiz configs from existing planner topics when re-importing the path.
 */
export function mergeCurriculumIntoPlannerTopics(
  imported: TimelineTopicItem[],
  existing: TimelineTopicItem[],
): TimelineTopicItem[] {
  const byId = new Map(existing.map((t) => [t.id, t]));
  return normalizeTopicQuizPlan(
    imported.map((t, index) => {
      const prev = byId.get(t.id);
      if (!prev) {
        return { ...t, index: index + 1 };
      }
      return {
        ...prev,
        id: t.id,
        index: index + 1,
        title: t.title,
        description: t.description || prev.description,
        durationMinutes: t.durationMinutes,
        workshopDayIndex:
          t.workshopDayIndex ?? prev.workshopDayIndex,
        // Topic-level quizzes only when there are no subtopics
        preQuiz: t.subtopics?.length ? undefined : prev.preQuiz,
        postQuiz: t.subtopics?.length ? undefined : prev.postQuiz,
        subtopics: mergeSubtopicLists(t.subtopics, prev.subtopics),
      };
    }),
  );
}

export function consumeCurriculumPathForTimeline(): TimelineTopicItem[] | null {
  try {
    const topics = loadCurriculumPath();
    if (topics.length === 0) return null;
    sessionStorage.removeItem(PATH_KEY);
    return curriculumPathToTimelineTopics(topics);
  } catch {
    return null;
  }
}

export function allTopicsHaveDuration(
  topics: {
    durationMinutes: number | null | undefined;
    subtopics?: { selected?: boolean; durationMinutes: number }[];
  }[],
): boolean {
  if (topics.length === 0) return false;
  return topics.every((t) => {
    // Curriculum TopicSubtopic uses `selected`; planner items omit it (all active).
    const hasSelectedFlag = t.subtopics?.some(
      (s) => typeof s.selected === 'boolean',
    );
    const selectedSubs = hasSelectedFlag
      ? (t.subtopics ?? []).filter((s) => s.selected)
      : t.subtopics ?? [];
    // Every topic must include at least one subtopic with a planned duration.
    if (selectedSubs.length === 0) return false;
    return selectedSubs.every((s) => (s.durationMinutes || 0) > 0);
  });
}
